/**
 * Adapter functions that convert different data sources into the
 * normalized PatientPanelData shape consumed by the unified PatientPanel.
 *
 * Two sources:
 *  1. fromExtractions — API extraction pipeline (PatientClinicalData)
 *  2. fromParserHook — case-panel agent text parsing (UsePatientPanelReturn)
 *
 * Pure functions — no React, no hooks, no side effects.
 */

import type { PatientPanelData, MedsStatus } from './types'
import type {
  PatientClinicalData,
  SemanticExtraction,
} from '../../contexts/ChatContext'
import { parseVitals, parseLabs, parseImaging, parsePanelResponse } from '../../utils/clinicalDataParser'
import type {
  VitalSign,
  LabGroup,
  ImagingFinding,
  PanelOrders,
  PanelSummary,
  MedicationCategories,
  OrderSection,
} from '../../utils/clinicalDataParser'
import type {
  VitalSignData,
  LabGroupData,
  ImagingData,
  OrdersData,
  MedicationsData,
  HydratedClinicalData,
} from '../../services/workspaceService'

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

/** Filter extractions by one or more categories and concatenate their content. */
function concatContent(
  extractions: SemanticExtraction[],
  ...categories: SemanticExtraction['category'][]
): string {
  return extractions
    .filter((e) => categories.includes(e.category))
    .map((e) => e.content)
    .join('\n')
}

/** Map a SemanticExtraction change_status to a MedicationCategories bucket key. */
function medBucket(
  status: SemanticExtraction['change_status'],
): keyof Pick<MedicationCategories, 'continue' | 'hold' | 'adjust' | 'discontinue'> {
  switch (status) {
    case 'held':
      return 'hold'
    case 'changed':
    case 'new':
      return 'adjust'
    case 'discontinued':
      return 'discontinue'
    case 'continued':
    default:
      return 'continue'
  }
}

/**
 * Order category label for grouping procedure/plan extractions into
 * PanelOrders sections.
 */
function orderSectionLabel(e: SemanticExtraction): string {
  const lower = e.content.toLowerCase()
  if (/\blab\b|blood|cbc|bmp|cmp|panel|culture/i.test(lower)) return 'Lab Orders'
  if (/\bimag|x-?ray|ct\b|mri|ultrasound|echo|cxr/i.test(lower)) return 'Imaging'
  if (/\bprocedure|intubat|line|drain|biopsy|surgery/i.test(lower)) return 'Procedures'
  return 'Other Orders'
}

// ────────────────────────────────────────────────────────
// Structured data mapping (JSONB → panel types)
// ────────────────────────────────────────────────────────

const VITAL_ICONS: Record<string, string> = {
  Temp: '\uD83C\uDF21\uFE0F',
  HR: '\u2764\uFE0F',
  BP: '\uD83E\uDEC0',
  RR: '\uD83E\uDEC1',
  SpO2: '\uD83D\uDCA7',
  FiO2: '\uD83C\uDF2C\uFE0F',
  Weight: '\u2696\uFE0F',
  Pain: '\uD83D\uDE23',
}

function mapStructuredVitals(data: VitalSignData[]): VitalSign[] {
  return data.map((v) => {
    let flag: 'high' | 'low' | null = null
    const numVal = parseFloat(v.value)
    if (!isNaN(numVal)) {
      if (v.ref_high !== null && numVal > v.ref_high) flag = 'high'
      else if (v.ref_low !== null && numVal < v.ref_low) flag = 'low'
    }
    return {
      label: v.label,
      value: v.value,
      unit: v.unit,
      flag,
      icon: VITAL_ICONS[v.label] ?? '',
      refRange: v.ref_range ?? (v.ref_low !== null && v.ref_high !== null ? `${v.ref_low}\u2013${v.ref_high}` : null),
    }
  })
}

function mapStructuredLabs(data: LabGroupData[]): LabGroup[] {
  return data.map((g) => ({
    name: g.group,
    labs: g.tests.map((t) => ({
      label: t.name,
      value: t.value,
      unit: t.unit,
      flag: t.flag,
      refRange: t.ref_range,
    })),
  }))
}

function mapStructuredImaging(data: ImagingData[]): ImagingFinding[] {
  return data.map((i) => ({ modality: i.modality, finding: i.finding }))
}

function mapStructuredOrders(data: OrdersData): PanelOrders {
  return {
    raw: data.sections.map((s) => `### ${s.title}\n${s.items.join('\n')}`).join('\n\n'),
    sections: data.sections.map((s) => ({
      title: s.title,
      content: s.items.join('\n'),
      items: s.items,
    })),
  }
}

function mapStructuredMeds(data: MedicationsData): MedicationCategories {
  return {
    continue: data.continue ?? [],
    hold: data.hold ?? [],
    adjust: data.adjust ?? [],
    discontinue: data.discontinue ?? [],
    drug_interactions: data.drug_interactions ?? [],
    prophylaxis_gaps: data.prophylaxis_gaps ?? [],
  }
}

// ────────────────────────────────────────────────────────
// fromExtractions
// ────────────────────────────────────────────────────────

/**
 * Convert PatientClinicalData (from the backend extraction pipeline)
 * into the normalized PatientPanelData shape.
 *
 * Priority chain for each section: structured JSONB → panel_content regex → extraction fallback.
 */
export function fromExtractions(
  data: PatientClinicalData,
  ipassSource?: string | null,
  isGenerating?: boolean,
  panelContent?: string | null,
  structuredData?: Partial<Pick<HydratedClinicalData, 'vitals' | 'labs' | 'imaging' | 'orders' | 'medications' | 'summary_presentation' | 'summary_admission_context'>> | null,
): PatientPanelData {
  const { extractions } = data

  // ── Parse panel_content as fallback (always available per-section) ──
  const panelParsed = panelContent ? parsePanelResponse(panelContent) : null

  // ── Vitals ──────────────────────────────────────────
  const structVitals = structuredData?.vitals && structuredData.vitals.length > 0
    ? mapStructuredVitals(structuredData.vitals as VitalSignData[])
    : null
  const vitalsText = concatContent(extractions, 'vital')
  const vitals = structVitals ?? panelParsed?.vitals ?? parseVitals(vitalsText || null)

  // ── Labs ────────────────────────────────────────────
  const structLabs = structuredData?.labs && structuredData.labs.length > 0
    ? mapStructuredLabs(structuredData.labs as LabGroupData[])
    : null
  const labsText = concatContent(extractions, 'lab')
  const labs = structLabs ?? panelParsed?.labs ?? parseLabs(labsText || null)

  // ── Imaging ─────────────────────────────────────────
  const structImaging = structuredData?.imaging && structuredData.imaging.length > 0
    ? mapStructuredImaging(structuredData.imaging as ImagingData[])
    : null
  const imagingText = concatContent(extractions, 'imaging')
  const imaging = structImaging ?? panelParsed?.imaging ?? parseImaging(imagingText || null)

  // ── Orders ──────────────────────────────────────────
  const structOrders = structuredData?.orders && (structuredData.orders as OrdersData).sections?.length > 0
    ? mapStructuredOrders(structuredData.orders as OrdersData)
    : null
  let orders: PanelOrders | null = structOrders ?? panelParsed?.orders ?? null
  if (!orders) {
    const orderExtractions = extractions.filter(
      (e) => e.category === 'procedure' || e.category === 'plan',
    )
    if (orderExtractions.length > 0) {
      const groups = new Map<string, string[]>()
      for (const e of orderExtractions) {
        const label = orderSectionLabel(e)
        const items = groups.get(label) ?? []
        items.push(e.content)
        groups.set(label, items)
      }

      const sectionOrder = ['Lab Orders', 'Imaging', 'Procedures', 'Other Orders']
      const sections: OrderSection[] = []
      for (const title of sectionOrder) {
        const items = groups.get(title)
        if (items && items.length > 0) {
          sections.push({ title, content: items.join('\n'), items })
        }
      }

      if (sections.length > 0) {
        orders = { raw: orderExtractions.map((e) => e.content).join('\n'), sections }
      }
    }
  }

  // ── Medications ─────────────────────────────────────
  const structMeds = structuredData?.medications && (
    (structuredData.medications as MedicationsData).continue?.length > 0 ||
    (structuredData.medications as MedicationsData).hold?.length > 0 ||
    (structuredData.medications as MedicationsData).adjust?.length > 0 ||
    (structuredData.medications as MedicationsData).discontinue?.length > 0
  )
    ? mapStructuredMeds(structuredData.medications as MedicationsData)
    : null
  let medications: MedicationCategories | null = structMeds ?? panelParsed?.medications ?? null
  if (!medications) {
    const medExtractions = extractions.filter((e) => e.category === 'medication')
    if (medExtractions.length > 0) {
      const meds: MedicationCategories = {
        continue: [], hold: [], adjust: [], discontinue: [],
        drug_interactions: [], prophylaxis_gaps: [],
      }

      for (const e of medExtractions) {
        const bucket = medBucket(e.change_status)
        const label = e.is_high_risk ? `${e.content} \u26A0\uFE0F` : e.content
        meds[bucket].push(label)
      }

      const hasAny = Object.values(meds).some((arr) => arr.length > 0)
      medications = hasAny ? meds : null
    }
  }

  // ── Summary ─────────────────────────────────────────
  let summary: PanelSummary | null = null
  if (structuredData?.summary_presentation || structuredData?.summary_admission_context) {
    summary = {
      raw: [data.patient_summary, structuredData.summary_presentation, structuredData.summary_admission_context].filter(Boolean).join('\n\n'),
      oneLiner: data.patient_summary || '',
      presentation: structuredData.summary_presentation || '',
      admissionContext: structuredData.summary_admission_context || '',
    }
  } else if (panelParsed?.summary) {
    summary = panelParsed.summary
  } else {
    const problemContent = concatContent(extractions, 'problem')
    summary = data.patient_summary
      ? {
          raw: data.patient_summary + (problemContent ? `\n\n${problemContent}` : ''),
          oneLiner: data.patient_summary,
          presentation: problemContent || '',
          admissionContext: '',
        }
      : null
  }

  // ── Assemble ────────────────────────────────────────
  return {
    summary,
    vitals,
    labs,
    imaging,
    orders,
    medications,
    medsStatus: data.med_recon_status as MedsStatus ?? (medications ? 'preliminary' : null),
    ipassSource: ipassSource ?? null,
    isGenerating: isGenerating ?? false,
    meta: {
      patientSummary: data.patient_summary,
      allergies: data.allergies,
      codeStatus: data.code_status,
      extractions: data.extractions,
      allergyConflicts: data.allergy_conflicts,
    },
  }
}

// ────────────────────────────────────────────────────────
// fromParserHook
// ────────────────────────────────────────────────────────
